import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, PrimaryButton, Modal, Field, TextInput, StatusPill, useFetch } from "../../components/crm/ui";
import { Plus, Trash2, Edit2, Building2, User, Search, Phone, Mail, KeyRound, X } from "lucide-react";

const EMPTY = {
  type: "service_franchisee",
  email: "", phone: "", password: "",
  contact_person: "", firm_name: "", gstin: "", pan: "",
  office_address: "", total_technicians: "", experience: "",
  name: "",
};

export default function ServiceTeam() {
  const { data: members, reload } = useFetch("/service-team");
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) =>
      [m.firm_name, m.name, m.contact_person, m.email, m.phone].join(" ").toLowerCase().includes(q)
    );
  }, [members, search]);

  function openCreate() { setEditing(null); setForm(EMPTY); setModalOpen(true); }
  function openEdit(m) {
    setEditing(m);
    setForm({ ...EMPTY, ...m, password: "" });
    setModalOpen(true);
  }
  function up(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function save() {
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.total_technicians) payload.total_technicians = Number(form.total_technicians);
      if (editing) {
        delete payload.email; delete payload.type;
        if (!payload.password) delete payload.password;
        await api.put(`/service-team/${editing.id}`, payload);
        toast.success("Service team member updated");
      } else {
        await api.post("/service-team", payload);
        toast.success("Service team member created — login credentials: " + form.email + " / " + form.password);
      }
      setModalOpen(false);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  }

  async function remove(m) {
    if (!confirm(`Delete ${m.firm_name || m.name}? This also removes their login.`)) return;
    try {
      await api.delete(`/service-team/${m.id}`);
      toast.success("Deleted");
      reload();
    } catch (err) { toast.error(err.message); }
  }

  return (
    <div className="space-y-6" data-testid="service-team-page">
      <PageHeader
        title="Service Team"
        subtitle="Franchisees & Service Managers who run installations and AMC tickets"
        right={
          <PrimaryButton onClick={openCreate} data-testid="service-team-add-btn">
            <Plus className="w-4 h-4" /> Add Member
          </PrimaryButton>
        }
      />

      <div className="bg-white border border-[#DDE5D8]">
        <div className="px-4 py-3 border-b border-[#DDE5D8] flex items-center gap-3 bg-[#F4F6F1]">
          <div className="relative flex-1 max-w-md">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by firm / name / email / phone…"
              data-testid="service-team-search"
              className="w-full pl-8 pr-2 py-1.5 text-xs font-mono border border-[#DDE5D8] focus:border-[#1F4E2C] outline-none bg-white"
            />
          </div>
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#475569] ml-auto">
            <span className="text-[#0F1A12] font-semibold">{filtered.length}</span> / {members.length}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0E2818] text-[#D4FF7A]">
              <tr>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Type</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Name / Firm</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Contact</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">GSTIN / PAN</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Techs</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Login</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.id} className="border-b border-[#EEF2EA] hover:bg-[#F4F6F1]" data-testid={`service-team-row-${m.id}`}>
                  <td className="px-4 py-3 align-top">
                    {m.type === "service_franchisee" ? (
                      <StatusPill value="Franchisee" tone="info" />
                    ) : (
                      <StatusPill value="Manager" tone="success" />
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="font-display font-semibold text-[#0F1A12]">
                      {m.firm_name || m.name || "—"}
                    </div>
                    {m.contact_person && (
                      <div className="font-mono text-[11px] text-[#475569] mt-0.5">Contact: {m.contact_person}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top font-mono text-[11px] text-[#475569]">
                    <div className="flex items-center gap-1"><Mail className="w-3 h-3" />{m.email}</div>
                    <div className="flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" />{m.phone}</div>
                  </td>
                  <td className="px-4 py-3 align-top font-mono text-[11px]">
                    {m.gstin && <div className="text-[#0F1A12]">{m.gstin}</div>}
                    {m.pan && <div className="text-[#475569] mt-0.5">{m.pan}</div>}
                    {!m.gstin && !m.pan && <span className="text-[#94A3B8]">—</span>}
                  </td>
                  <td className="px-4 py-3 align-top font-mono text-xs text-[#0F1A12]">
                    {m.total_technicians ?? "—"}
                  </td>
                  <td className="px-4 py-3 align-top font-mono text-[11px] text-[#94A3B8]">
                    <span className="inline-flex items-center gap-1"><KeyRound className="w-3 h-3" />Active</span>
                  </td>
                  <td className="px-4 py-3 align-top text-right">
                    <button onClick={() => openEdit(m)} data-testid={`service-team-edit-${m.id}`}
                      className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-[#DDE5D8] hover:border-[#1F4E2C] hover:text-[#1F4E2C] mr-1">
                      <Edit2 className="w-3 h-3" /> Edit
                    </button>
                    <button onClick={() => remove(m)} data-testid={`service-team-delete-${m.id}`}
                      className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-[#FCD7D7] text-[#B91C1C] hover:bg-[#FCD7D7]">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-sm font-mono text-[#94A3B8]">
                  {members.length === 0 ? "No service team members yet." : "No members match your search."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Edit Service Team Member" : "Add Service Team Member"}>
        <div className="space-y-4">
          {!editing && (
            <Field label="Member Type">
              <div className="flex gap-2">
                <button type="button" onClick={() => up("type", "service_franchisee")}
                  className={"flex-1 px-3 py-2 text-xs font-mono uppercase tracking-widest border " + (form.type === "service_franchisee" ? "border-[#1F4E2C] bg-[#1F4E2C] text-white" : "border-[#DDE5D8] text-[#475569]")}>
                  <Building2 className="w-3.5 h-3.5 inline mr-1.5" />Service Franchisee (Vendor)
                </button>
                <button type="button" onClick={() => up("type", "service_manager")}
                  className={"flex-1 px-3 py-2 text-xs font-mono uppercase tracking-widest border " + (form.type === "service_manager" ? "border-[#1F4E2C] bg-[#1F4E2C] text-white" : "border-[#DDE5D8] text-[#475569]")}>
                  <User className="w-3.5 h-3.5 inline mr-1.5" />Service Manager (In-house)
                </button>
              </div>
            </Field>
          )}

          <div className="grid grid-cols-2 gap-3">
            {!editing && <Field label="Email *"><TextInput type="email" value={form.email} onChange={(e) => up("email", e.target.value)} data-testid="st-email" /></Field>}
            <Field label="Phone *"><TextInput value={form.phone} onChange={(e) => up("phone", e.target.value)} data-testid="st-phone" /></Field>
          </div>

          {form.type === "service_franchisee" ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Firm Name *"><TextInput value={form.firm_name} onChange={(e) => up("firm_name", e.target.value)} data-testid="st-firm" /></Field>
                <Field label="Contact Person *"><TextInput value={form.contact_person} onChange={(e) => up("contact_person", e.target.value)} data-testid="st-contact" /></Field>
                <Field label="GSTIN"><TextInput value={form.gstin} onChange={(e) => up("gstin", e.target.value)} /></Field>
                <Field label="PAN"><TextInput value={form.pan} onChange={(e) => up("pan", e.target.value)} /></Field>
                <Field label="Total Technicians"><TextInput type="number" value={form.total_technicians} onChange={(e) => up("total_technicians", e.target.value)} /></Field>
                <Field label="Experience"><TextInput value={form.experience} onChange={(e) => up("experience", e.target.value)} placeholder="e.g. 5 years" /></Field>
              </div>
              <Field label="Office Address"><TextInput value={form.office_address} onChange={(e) => up("office_address", e.target.value)} /></Field>
            </>
          ) : (
            <Field label="Name *"><TextInput value={form.name} onChange={(e) => up("name", e.target.value)} data-testid="st-name" /></Field>
          )}

          <Field label={editing ? "Password (leave blank to keep current)" : "Password *"}>
            <TextInput type="text" value={form.password} onChange={(e) => up("password", e.target.value)} data-testid="st-password" />
          </Field>
          {!editing && (
            <p className="text-[11px] font-mono text-[#475569] bg-[#FAFBF7] border border-[#DDE5D8] p-2">
              Share these credentials with the member manually so they can log in at <code>/login</code>.
              They'll see only their assigned tickets and technicians.
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setModalOpen(false)} className="text-xs font-mono uppercase tracking-widest px-3 py-1.5 border border-[#DDE5D8] text-[#475569] hover:border-[#1F4E2C] hover:text-[#1F4E2C]">Cancel</button>
            <PrimaryButton onClick={save} disabled={saving} data-testid="st-save">
              {saving ? "Saving…" : (editing ? "Save changes" : "Create member")}
            </PrimaryButton>
          </div>
        </div>
      </Modal>
    </div>
  );
}
